import { useState, type ReactNode } from 'react';
import type { DashboardResponse } from '@hesabat/contracts';
import { Money } from '@hesabat/finance-core';
import { formatMoney } from './money';
import { formatCompact, monthLabel } from './dashboardText';

/**
 * رسوم اللوحة — SVG مباشر بلا مكتبة رسوم حتى تبقى الحزمة خفيفة. المبالغ تبقى `Money`
 * حتى لحظة تحويلها إلى بكسلات، ولا يُحسب رقم معروض من بكسل. الحركة (نمو الأعمدة ورسم
 * الخطوط) فى dashboard.css وتُلغى مع تقليل الحركة.
 */

/** موضع قيمة على محور من `min` إلى `max` بالبكسل (الأعلى أصغر). */
function scaleY(value: string, min: Money, max: Money, height: number): number {
  const span = max.sub(min);
  if (span.isZero()) return height;
  const ratio = Money.of(value).sub(min).mul(height).div(span);
  return height - Number(ratio.toFixed(2));
}

function extent(values: readonly string[], includeZero = true): { min: Money; max: Money } {
  let min = includeZero ? Money.zero() : Money.of(values[0] ?? '0');
  let max = includeZero ? Money.zero() : Money.of(values[0] ?? '0');
  for (const value of values) {
    const amount = Money.of(value);
    if (amount.gt(max)) max = amount;
    if (amount.lt(min)) min = amount;
  }
  return { min, max };
}

/** خط صغير بلا محاور للبطاقة — الأقدم يمينًا كترتيب القراءة. */
export function Sparkline({
  values,
  tone = 'primary',
  label,
}: {
  values: readonly (string | null)[];
  tone?: 'primary' | 'good' | 'bad';
  label: string;
}) {
  const points = values
    .map((value, index) => ({ value, index }))
    .filter((point): point is { value: string; index: number } => point.value !== null);
  if (points.length < 2) return <span className="dash-spark dash-spark-empty" aria-hidden />;
  const width = 120;
  const height = 34;
  const { min, max } = extent(
    points.map((point) => point.value),
    false,
  );
  const step = width / Math.max(values.length - 1, 1);
  const coords = points.map((point) => ({
    x: width - point.index * step,
    y: 3 + scaleY(point.value, min, max, height - 6),
  }));
  const line = coords.map((c, i) => (i === 0 ? 'M' : 'L') + c.x + ' ' + c.y).join(' ');
  const last = coords[coords.length - 1]!;
  const area = line + ' L' + last.x + ' ' + height + ' L' + coords[0]!.x + ' ' + height + ' Z';
  return (
    <svg
      className={'dash-spark tone-' + tone}
      viewBox={'0 0 ' + width + ' ' + height}
      role="img"
      aria-label={label}
      preserveAspectRatio="none"
    >
      <path d={area} className="dash-spark-area" />
      <path d={line} pathLength={1} className="dash-spark-line" />
      <circle cx={last.x} cy={last.y} r={3} className="dash-spark-dot" />
    </svg>
  );
}

function Tooltip({ left, children }: { left: number; children: ReactNode }) {
  // يبقى داخل الإطار: لا يتجاوز الحافتين
  const clamped = Math.min(Math.max(left, 12), 88);
  return (
    <div className="dash-tooltip" style={{ left: clamped + '%' }} role="status">
      {children}
    </div>
  );
}

const PLOT_HEIGHT = 170;
const AXIS_WIDTH = 46;
const GROUP_WIDTH = 52;
const BAR_WIDTH = 16;

function gridTicks(min: Money, max: Money): string[] {
  const ticks = [min, min.add(max.sub(min).div(2)), max].map((tick) => tick.round(0).toString());
  return [...new Set(ticks)];
}

function AxisGrid({
  min,
  max,
  plotWidth,
  width,
  yOf,
}: {
  min: Money;
  max: Money;
  plotWidth: number;
  width: number;
  yOf: (value: string) => number;
}) {
  return (
    <>
      {gridTicks(min, max).map((tick) => {
        const y = yOf(tick);
        return (
          <g key={tick}>
            <line x1={0} x2={plotWidth} y1={y} y2={y} className="dash-grid" />
            <text
              x={width - 2}
              y={y + 4}
              textAnchor="end"
              direction="ltr"
              className="dash-axis-label"
            >
              {formatCompact(tick)}
            </text>
          </g>
        );
      })}
    </>
  );
}

interface BarSeries<T> {
  label: string;
  value: (row: T) => string;
  /** لون السلسلة: `bar-…` للعمود و`key-…` لمفتاحه. */
  tone: 'revenue' | 'expense';
}

/**
 * عمودان لكل شهر بمحور واحد ولمس يُظهر الأرقام — الإيراد مقابل المصروف، والمبيعات مقابل
 * المشتريات. الأعمدة تنمو من خط الصفر عند الظهور.
 */
export function MonthlyBarsChart<T extends { month: string }>({
  rows,
  series,
  ariaLabel,
  footer,
}: {
  rows: readonly T[];
  series: readonly [BarSeries<T>, BarSeries<T>];
  ariaLabel: string;
  /** سطر إضافى فى التلميح (صافى الربح مثلاً). */
  footer?: (row: T) => ReactNode;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const [first, second] = series;
  const { min, max } = extent(rows.flatMap((row) => [first.value(row), second.value(row)]));
  const plotWidth = rows.length * GROUP_WIDTH;
  const width = plotWidth + AXIS_WIDTH;
  const yOf = (value: string) => scaleY(value, min, max, PLOT_HEIGHT);
  const zeroY = yOf('0');
  const xOf = (index: number) => plotWidth - (index + 1) * GROUP_WIDTH;
  const bar = (value: string, x: number, tone: BarSeries<T>['tone'], index: number) => {
    const y = yOf(value);
    const top = Math.min(y, zeroY);
    const height = Math.max(Math.abs(zeroY - y), Money.of(value).isZero() ? 0 : 1);
    return (
      <rect
        x={x}
        y={top}
        width={BAR_WIDTH}
        height={height}
        rx={3}
        className={'dash-bar bar-' + tone}
        // الأحدث (يسارًا) ينمو أخيرًا: العين تنتهى عند الشهر الحالى
        style={{ animationDelay: index * 30 + 'ms' }}
      />
    );
  };
  const hovered = hover === null ? null : rows[hover];

  return (
    <figure className="dash-chart">
      <div className="dash-chart-legend">
        {series.map((item) => (
          <span key={item.label}>
            <i className={'dash-key key-' + item.tone} /> {item.label}
          </span>
        ))}
      </div>
      <div className="dash-plot" onMouseLeave={() => setHover(null)}>
        <svg viewBox={'0 0 ' + width + ' ' + (PLOT_HEIGHT + 26)} role="img" aria-label={ariaLabel}>
          <AxisGrid min={min} max={max} plotWidth={plotWidth} width={width} yOf={yOf} />
          {rows.map((row, index) => {
            const x = xOf(index) + (GROUP_WIDTH - BAR_WIDTH * 2 - 2) / 2;
            return (
              <g key={row.month} className={hover === index ? 'is-hover' : undefined}>
                {bar(second.value(row), x, second.tone, index)}
                {bar(first.value(row), x + BAR_WIDTH + 2, first.tone, index)}
                {index % 2 === (rows.length - 1) % 2 && (
                  <text
                    x={xOf(index) + GROUP_WIDTH / 2}
                    y={PLOT_HEIGHT + 20}
                    textAnchor="middle"
                    className="dash-axis-label"
                  >
                    {monthLabel(row.month)}
                  </text>
                )}
                <rect
                  x={xOf(index)}
                  y={0}
                  width={GROUP_WIDTH}
                  height={PLOT_HEIGHT + 26}
                  className="dash-hit"
                  onMouseEnter={() => setHover(index)}
                  onClick={() => setHover(index)}
                >
                  <title>
                    {monthLabel(row.month) +
                      ': ' +
                      first.label +
                      ' ' +
                      formatMoney(first.value(row)) +
                      '، ' +
                      second.label +
                      ' ' +
                      formatMoney(second.value(row))}
                  </title>
                </rect>
              </g>
            );
          })}
        </svg>
        {hovered && hover !== null && (
          <Tooltip left={((xOf(hover) + GROUP_WIDTH / 2) / width) * 100}>
            <strong>
              {monthLabel(hovered.month)} {hovered.month.slice(0, 4)}
            </strong>
            {series.map((item) => (
              <span key={item.label}>
                <i className={'dash-key key-' + item.tone} /> {item.label}{' '}
                {formatMoney(item.value(hovered))}
              </span>
            ))}
            {footer?.(hovered)}
          </Tooltip>
        )}
      </div>
    </figure>
  );
}

/** الإيراد مقابل المصروف من قائمة الدخل لكل شهر. */
export function RevenueExpenseChart({
  data,
  currency,
}: {
  data: DashboardResponse['monthly'];
  currency: string;
}) {
  return (
    <MonthlyBarsChart
      rows={data}
      ariaLabel="الإيراد والمصروف لآخر ١٢ شهرًا"
      series={[
        { label: 'الإيراد', value: (row) => row.revenue, tone: 'revenue' },
        {
          label: 'المصروف (تكلفة المبيعات + التشغيل)',
          value: (row) => row.expenses,
          tone: 'expense',
        },
      ]}
      footer={(row) => (
        <span>
          صافى الربح {formatMoney(row.netProfit)} {currency}
        </span>
      )}
    />
  );
}

/** المبيعات مقابل المشتريات بالمستندات لكل شهر — رقما تحليلى مركز التقارير. */
export function TradeChart({
  data,
  currency,
}: {
  data: DashboardResponse['trade'];
  currency: string;
}) {
  return (
    <MonthlyBarsChart
      rows={data}
      ariaLabel="صافى المبيعات وصافى المشتريات لآخر ١٢ شهرًا"
      series={[
        { label: 'صافى المبيعات', value: (row) => row.sales, tone: 'revenue' },
        { label: 'صافى المشتريات', value: (row) => row.purchases, tone: 'expense' },
      ]}
      footer={(row) => (
        <span>
          الفرق {formatMoney(Money.of(row.sales).sub(row.purchases))} {currency}
        </span>
      )}
    />
  );
}

interface TrendPoint {
  month: string;
  value: string | null;
}

/**
 * خط شهرى بمساحة خفيفة وخط تتبع عند اللمس — رصيد النقدية أو هامش الربح. الشهر بلا قيمة
 * (هامش بلا إيراد) فجوة فى الخط لا صفر. الخط يُرسم من أوله عند الظهور.
 */
export function TrendLineChart({
  points,
  ariaLabel,
  format,
  tooltipTitle,
  tone = 'revenue',
  includeZero = true,
}: {
  points: readonly TrendPoint[];
  ariaLabel: string;
  format: (value: string) => string;
  tooltipTitle: (month: string) => string;
  tone?: 'revenue' | 'line';
  /** الأرصدة تبدأ من الصفر؛ النسبة (الهامش) تُرسم على مداها حتى يظهر تغيّرها. */
  includeZero?: boolean;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const known = points.flatMap((point) => (point.value === null ? [] : [point.value]));
  const { min, max } = extent(known, includeZero);
  const plotWidth = points.length * GROUP_WIDTH;
  const width = plotWidth + AXIS_WIDTH;
  const xOf = (index: number) => plotWidth - index * GROUP_WIDTH - GROUP_WIDTH / 2;
  const yOf = (value: string) => 6 + scaleY(value, min, max, PLOT_HEIGHT - 6);
  const coords = points.map((point, index) =>
    point.value === null ? null : { x: xOf(index), y: yOf(point.value) },
  );
  const zeroY = yOf('0');
  // كل جزء متصل يبدأ بـ M — الفجوة تقطع الخط
  const line = coords
    .map((coord, index) =>
      coord ? (index === 0 || !coords[index - 1] ? 'M' : 'L') + coord.x + ' ' + coord.y : '',
    )
    .filter(Boolean)
    .join(' ');
  const present = coords.flatMap((coord) => (coord ? [coord] : []));
  const firstCoord = present[0];
  const lastCoord = present[present.length - 1];
  const area =
    present.length === coords.length && firstCoord && lastCoord
      ? line + ' L' + lastCoord.x + ' ' + zeroY + ' L' + firstCoord.x + ' ' + zeroY + ' Z'
      : '';
  const active = hover === null ? null : { point: points[hover]!, at: coords[hover] ?? null };

  return (
    <figure className="dash-chart">
      <div className="dash-plot" onMouseLeave={() => setHover(null)}>
        <svg viewBox={'0 0 ' + width + ' ' + (PLOT_HEIGHT + 26)} role="img" aria-label={ariaLabel}>
          <AxisGrid min={min} max={max} plotWidth={plotWidth} width={width} yOf={yOf} />
          {area && <path d={area} className={'dash-trend-area tone-' + tone} />}
          {line && <path d={line} pathLength={1} className={'dash-trend-line tone-' + tone} />}
          {active?.at && (
            <line
              x1={active.at.x}
              x2={active.at.x}
              y1={0}
              y2={PLOT_HEIGHT}
              className="dash-crosshair"
            />
          )}
          {lastCoord && (
            <circle
              cx={(active?.at ?? lastCoord).x}
              cy={(active?.at ?? lastCoord).y}
              r={4.5}
              className={'dash-trend-dot tone-' + tone}
            />
          )}
          {points.map((point, index) => (
            <g key={point.month}>
              {index % 2 === (points.length - 1) % 2 && (
                <text
                  x={xOf(index)}
                  y={PLOT_HEIGHT + 20}
                  textAnchor="middle"
                  className="dash-axis-label"
                >
                  {monthLabel(point.month)}
                </text>
              )}
              <rect
                x={xOf(index) - GROUP_WIDTH / 2}
                y={0}
                width={GROUP_WIDTH}
                height={PLOT_HEIGHT + 26}
                className="dash-hit"
                onMouseEnter={() => setHover(index)}
                onClick={() => setHover(index)}
              >
                <title>
                  {monthLabel(point.month) +
                    ': ' +
                    (point.value === null ? '—' : format(point.value))}
                </title>
              </rect>
            </g>
          ))}
        </svg>
        {active && hover !== null && (
          <Tooltip left={(xOf(hover) / width) * 100}>
            <strong>{tooltipTitle(active.point.month)}</strong>
            <span>
              {active.point.value === null ? 'لا قيمة لهذا الشهر' : format(active.point.value)}
            </span>
          </Tooltip>
        )}
      </div>
    </figure>
  );
}

/** رصيد النقدية والبنوك فى نهاية كل شهر. */
export function CashTrendChart({
  data,
  currency,
}: {
  data: DashboardResponse['cashTrend'];
  currency: string;
}) {
  return (
    <TrendLineChart
      points={data.map((point) => ({ month: point.month, value: point.balance }))}
      ariaLabel="رصيد النقدية والبنوك فى نهاية كل شهر"
      format={(value) => formatMoney(value) + ' ' + currency}
      tooltipTitle={(month) => 'نهاية ' + monthLabel(month) + ' ' + month.slice(0, 4)}
    />
  );
}

/** هامش مجمل الربح لكل شهر — نفس أرقام قائمة الدخل التى يُرسم منها خط بطاقة الربح. */
export function MarginTrendChart({
  months,
  margins,
}: {
  months: readonly string[];
  margins: readonly (string | null)[];
}) {
  return (
    <TrendLineChart
      points={months.map((month, index) => ({ month, value: margins[index] ?? null }))}
      ariaLabel="هامش مجمل الربح لكل شهر"
      format={(value) => value + '٪'}
      tooltipTitle={(month) => 'هامش ' + monthLabel(month) + ' ' + month.slice(0, 4)}
      tone="line"
      includeZero={false}
    />
  );
}

const AGING_SLICES = [
  { key: 'current', label: 'لم يستحق بعد' },
  { key: 'days1to30', label: 'متأخر ١–٣٠ يومًا' },
  { key: 'days31to60', label: '٣١–٦٠ يومًا' },
  { key: 'days61to90', label: '٦١–٩٠ يومًا' },
  { key: 'over90', label: 'أكثر من ٩٠ يومًا' },
] as const;

/** دائرة الأعمار (عملاء أو موردين) — شرائح موجبة فقط، وغير المخصص سطر تحتها لا شريحة. */
export function AgingDonut({
  aging,
  total,
  currency,
  onOpen,
  subject = 'العملاء',
  unappliedLabel = 'تحصيلات لم تُخصص (تُخصم)',
}: {
  aging: DashboardResponse['receivablesAging'];
  total: string;
  currency: string;
  onOpen: () => void;
  /** لمن الديون — «العملاء» أو «الموردين» فى نصوص قارئ الشاشة. */
  subject?: string;
  unappliedLabel?: string;
}) {
  const radius = 58;
  const circumference = 2 * Math.PI * radius;
  const positive = AGING_SLICES.map((slice, index) => ({
    ...slice,
    index,
    amount: Money.of(aging[slice.key]),
  })).filter((slice) => slice.amount.isPositive());
  const sum = Money.sum(positive.map((slice) => slice.amount));
  let offset = 0;
  const arcs = sum.isZero()
    ? []
    : positive.map((slice) => {
        const length = Number(slice.amount.mul(circumference).div(sum).toFixed(2));
        const gap = positive.length > 1 ? Math.min(2, length / 2) : 0;
        const arc = { ...slice, length: length - gap, offset };
        offset += length;
        return arc;
      });

  return (
    <div className="dash-donut-wrap">
      <button
        type="button"
        className="dash-donut"
        onClick={onOpen}
        aria-label={'فتح تقرير أعمار ' + subject}
      >
        <svg viewBox="0 0 150 150" role="img" aria-label={'أعمار ديون ' + subject}>
          <circle cx={75} cy={75} r={radius} className="dash-donut-track" />
          {arcs.map((arc) => (
            <circle
              key={arc.key}
              cx={75}
              cy={75}
              r={radius}
              className={'dash-donut-arc age-' + arc.index}
              strokeDasharray={arc.length + ' ' + circumference}
              strokeDashoffset={-arc.offset}
              transform="rotate(-90 75 75)"
            >
              <title>{arc.label + ': ' + formatMoney(arc.amount)}</title>
            </circle>
          ))}
        </svg>
        <span className="dash-donut-center">
          <small>المستحق</small>
          <strong>
            <bdi>{formatCompact(total)}</bdi>
          </strong>
          <small>{currency}</small>
        </span>
      </button>
      <ul className="dash-legend-list">
        {AGING_SLICES.map((slice, index) => (
          <li key={slice.key}>
            <i className={'dash-key age-' + index} />
            <span>{slice.label}</span>
            <b>{formatMoney(aging[slice.key])}</b>
          </li>
        ))}
        {!Money.of(aging.unappliedCredit).isZero() && (
          <li className="dash-legend-note">
            <span>{unappliedLabel}</span>
            <b>{formatMoney(Money.of(aging.unappliedCredit).negate())}</b>
          </li>
        )}
      </ul>
    </div>
  );
}

/** أكبر خمسة: شريط أفقى بطول نسبى للأكبر، والصف يفتح كشفه أو تقريره. */
export function RankedList({
  rows,
  empty,
  onOpen,
}: {
  rows: DashboardResponse['top']['customers'];
  empty: string;
  onOpen: (row: DashboardResponse['top']['customers'][number]) => void;
}) {
  if (rows.length === 0) return <p className="dash-empty-line">{empty}</p>;
  const max = rows.reduce((top, row) => {
    const amount = Money.of(row.amount);
    return amount.gt(top) ? amount : top;
  }, Money.zero());
  return (
    <ol className="dash-ranked">
      {rows.map((row, index) => {
        const share = max.isZero() ? 0 : Number(Money.of(row.amount).mul(100).div(max).toFixed(1));
        return (
          <li key={row.key} style={{ animationDelay: index * 50 + 'ms' }}>
            <button type="button" onClick={() => onOpen(row)}>
              <span className="dash-ranked-label">{row.label}</span>
              <span className="dash-ranked-amount">{formatMoney(row.amount)}</span>
              <span className="dash-ranked-track" aria-hidden>
                <span style={{ width: share + '%' }} />
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}
